// Submenú "Bancos y Cuentas" del módulo Control Bancario.
//   Bancos            → CRUD del catálogo de instituciones financieras
//   Cuentas           → CRUD de cuentas bancarias (por banco)
//   Saldos y posición → RPC cb_posicion (solo lectura)
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Banknote, Landmark, Loader2, Pencil, Plus, Scale, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  Banco, Campo, Cuenta, Modal, Switch, TIPO_BANCO, TIPO_CUENTA, cb, cbRpc, fieldCls, label, money,
} from './shared';

// ============================================================== BANCOS =======
const bancoVacio = { codigo: '', nombre: '', nombre_corto: '', tipo: 'banco', swift: '', pais: 'EC', es_activo: true };

export function Bancos() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Banco[]>({
    queryKey: ['cb-bancos'],
    queryFn: () => cb.list<Banco>('bancos', '?select=*&deleted_at=is.null&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof bancoVacio>(bancoVacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof bancoVacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (b?: Banco) => {
    if (b) { setEditId(b.id); setForm({ codigo: b.codigo ?? '', nombre: b.nombre, nombre_corto: b.nombre_corto ?? '', tipo: b.tipo, swift: b.swift ?? '', pais: b.pais ?? 'EC', es_activo: b.es_activo }); }
    else { setEditId(null); setForm(bancoVacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, codigo: form.codigo || null, nombre_corto: form.nombre_corto || null, swift: form.swift || null };
      return editId ? cb.update('bancos', editId, body) : cb.create('bancos', body);
    },
    onSuccess: () => { toast.success(t('bancos.guardado')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['cb-bancos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('bancos', id),
    onSuccess: () => { toast.success(t('bancos.eliminado')); void qc.invalidateQueries({ queryKey: ['cb-bancos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Landmark className="w-5 h-5" /> {t('bancos.titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('bancos.btn_nuevo')}</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('bancos.instituciones', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('bancos.col_codigo')}</th><th className="py-2 pr-3">{t('bancos.col_nombre')}</th><th className="py-2 pr-3">{t('bancos.col_tipo')}</th><th className="py-2 pr-3">{t('bancos.col_swift')}</th><th className="py-2 pr-3">{t('bancos.col_pais')}</th><th className="py-2 pr-3">{t('bancos.col_estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{t('bancos.vacio')}</td></tr>}
              {rows.map((b) => (
                <tr key={b.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{b.codigo ?? '—'}</td>
                  <td className="py-2 pr-3">{b.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{label(TIPO_BANCO, b.tipo)}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs">{b.swift ?? '—'}</td>
                  <td className="py-2 pr-3">{b.pais}</td>
                  <td className="py-2 pr-3">{b.es_activo ? t('bancos.activo') : t('bancos.inactivo')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(b)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('bancos.confirmar_eliminar', { nombre: b.nombre }), { peligro: true })) eliminar.mutate(b.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('bancos.editar_banco') : t('bancos.nuevo_banco')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('bancos.f_codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label={t('bancos.col_tipo')}>
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPO_BANCO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label={t('bancos.f_nombre')}><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label={t('bancos.f_nombre_corto')} className="col-span-2"><input className={fieldCls} value={form.nombre_corto} onChange={(e) => set({ nombre_corto: e.target.value })} /></Campo>
              <Campo label={t('bancos.col_pais')}><input className={fieldCls} maxLength={2} value={form.pais} onChange={(e) => set({ pais: e.target.value.toUpperCase() })} /></Campo>
            </div>
            <Campo label={t('bancos.f_swift')}><input className={fieldCls} value={form.swift} onChange={(e) => set({ swift: e.target.value.toUpperCase() })} /></Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('bancos.activo')} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================= CUENTAS =======
const cuentaVacia = {
  banco_id: '', numero: '', tipo: 'corriente', moneda: 'USD', alias: '', titular: '',
  cuenta_contable_id: '', saldo_inicial: 0, fecha_saldo_inicial: '', sobregiro_autorizado: 0, es_activo: true,
};

export function Cuentas() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: bancos = [] } = useQuery<Banco[]>({
    queryKey: ['cb-bancos'],
    queryFn: () => cb.list<Banco>('bancos', '?select=*&deleted_at=is.null&order=nombre'),
  });
  const { data: rows = [], isFetching } = useQuery<(Cuenta & { banco?: { nombre: string } })[]>({
    queryKey: ['cb-cuentas'],
    queryFn: () => cb.list('cuentas', '?select=*,banco:banco_id(nombre)&deleted_at=is.null&order=alias,numero'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof cuentaVacia>(cuentaVacia);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof cuentaVacia>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: Cuenta) => {
    if (c) {
      setEditId(c.id);
      setForm({
        banco_id: c.banco_id, numero: c.numero, tipo: c.tipo, moneda: c.moneda, alias: c.alias ?? '',
        titular: c.titular ?? '', cuenta_contable_id: c.cuenta_contable_id ?? '', saldo_inicial: c.saldo_inicial,
        fecha_saldo_inicial: c.fecha_saldo_inicial ?? '', sobregiro_autorizado: c.sobregiro_autorizado, es_activo: c.es_activo,
      });
    } else { setEditId(null); setForm({ ...cuentaVacia, banco_id: bancos[0]?.id ?? '' }); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        alias: form.alias || null, titular: form.titular || null, cuenta_contable_id: form.cuenta_contable_id || null,
        fecha_saldo_inicial: form.fecha_saldo_inicial || null,
        saldo_inicial: Number(form.saldo_inicial) || 0, sobregiro_autorizado: Number(form.sobregiro_autorizado) || 0,
      };
      return editId ? cb.update('cuentas', editId, body) : cb.create('cuentas', body);
    },
    onSuccess: () => { toast.success(t('cuentas.guardada')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['cb-cuentas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('cuentas', id),
    onSuccess: () => { toast.success(t('cuentas.eliminada')); void qc.invalidateQueries({ queryKey: ['cb-cuentas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> {t('comun.volver')}</Button>
          <h1 className="text-xl font-semibold">{editId ? t('cuentas.editar') : t('cuentas.nueva')}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold flex items-center gap-2"><Wallet className="w-5 h-5" /> {t('cuentas.titulo')}</h1>
          <Button onClick={() => abrir()} disabled={bancos.length === 0}><Plus className="w-4 h-4 mr-1" /> {t('cuentas.btn_nueva')}</Button>
        </div>
      )}
      {!abierto && (<>
      {bancos.length === 0 && <p className="text-sm text-muted-foreground">{t('cuentas.primero_banco')}</p>}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('cuentas.lista', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('cuentas.col_alias')}</th><th className="py-2 pr-3">{t('cuentas.col_banco')}</th><th className="py-2 pr-3">{t('cuentas.col_numero')}</th><th className="py-2 pr-3">{t('cuentas.col_tipo')}</th><th className="py-2 pr-3">{t('cuentas.col_moneda')}</th><th className="py-2 pr-3 text-right">{t('cuentas.col_saldo_inicial')}</th><th className="py-2 pr-3">{t('cuentas.col_estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{t('cuentas.vacio')}</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{c.alias ?? '—'}</td>
                  <td className="py-2 pr-3">{c.banco?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.numero}</td>
                  <td className="py-2 pr-3 capitalize">{c.tipo}</td>
                  <td className="py-2 pr-3">{c.moneda}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.saldo_inicial, c.moneda)}</td>
                  <td className="py-2 pr-3">{c.es_activo ? t('cuentas.activa') : t('cuentas.inactiva')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('comun.eliminar')} onClick={async () => { if (await dlg.confirmar(t('cuentas.confirmar_eliminar', { nombre: c.alias ?? c.numero }), { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('cuentas.f_banco')}>
                <select className={fieldCls} value={form.banco_id} onChange={(e) => set({ banco_id: e.target.value })}>
                  <option value="">{t('comun.selecciona')}</option>
                  {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Campo>
              <Campo label={t('cuentas.f_alias')}><input className={fieldCls} value={form.alias} onChange={(e) => set({ alias: e.target.value })} placeholder={t('cuentas.ph_alias')} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label={t('cuentas.f_numero')}><input className={fieldCls} value={form.numero} onChange={(e) => set({ numero: e.target.value })} /></Campo>
              <Campo label={t('cuentas.f_tipo')}>
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPO_CUENTA.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Campo>
              <Campo label={t('cuentas.f_moneda')}><input className={fieldCls} maxLength={3} value={form.moneda} onChange={(e) => set({ moneda: e.target.value.toUpperCase() })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('cuentas.f_titular')}><input className={fieldCls} value={form.titular} onChange={(e) => set({ titular: e.target.value })} /></Campo>
              <Campo label={t('cuentas.f_cuenta_contable')}>
                <BuscadorCuenta value={form.cuenta_contable_id} onChange={(id) => set({ cuenta_contable_id: id })} allowClear placeholder={t('cuentas.sin_mapear')} />
              </Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label={t('cuentas.f_saldo_inicial')}><input type="number" step="0.01" className={fieldCls} value={form.saldo_inicial} onChange={(e) => set({ saldo_inicial: Number(e.target.value) })} /></Campo>
              <Campo label={t('cuentas.f_fecha_saldo')}><input type="date" className={fieldCls} value={form.fecha_saldo_inicial} onChange={(e) => set({ fecha_saldo_inicial: e.target.value })} /></Campo>
              <Campo label={t('cuentas.f_sobregiro')}><input type="number" step="0.01" className={fieldCls} value={form.sobregiro_autorizado} onChange={(e) => set({ sobregiro_autorizado: Number(e.target.value) })} /></Campo>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('cuentas.activa')} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
                <Button isLoading={guardar.isPending} disabled={!form.banco_id || !form.numero.trim()} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================================================== SALDOS Y POSICIÓN =======
interface Posicion {
  cuentas: { id: string; banco: string; banco_corto: string; alias: string | null; numero: string; tipo: string; moneda: string; saldo: number; es_activo: boolean }[];
  por_moneda: { moneda: string; n: number; saldo: number }[];
  total_cuentas: number;
}

export function Posicion() {
  const { data, isFetching } = useQuery<Posicion>({
    queryKey: ['cb-posicion'],
    queryFn: () => cbRpc<Posicion>('cb_posicion'),
  });
  const cuentas = data?.cuentas ?? [];

  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cb-posicion" antes="Saldos y posición" />
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><Scale className="w-5 h-5" /> Posición de caja</h1>
        <p className="text-sm text-muted-foreground">Saldo según apertura de cada cuenta. Los movimientos se reflejarán al habilitar el submenú Movimientos.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(data?.por_moneda ?? []).map((m) => (
          <Card key={m.moneda}><CardContent className="py-3 flex items-start gap-3">
            <div className="text-primary mt-0.5"><Banknote className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Total {m.moneda} · {m.n} cuenta(s)</div>
              <div className="text-lg font-semibold font-mono tabular-nums truncate">{money(m.saldo, m.moneda)}</div>
            </div>
          </CardContent></Card>
        ))}
        {(data?.por_moneda?.length ?? 0) === 0 && !isFetching && (
          <p className="text-sm text-muted-foreground col-span-full">Aún no hay cuentas con saldo.</p>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cuentas ({data?.total_cuentas ?? 0})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Banco</th><th className="py-2 pr-3">Alias</th><th className="py-2 pr-3">Número</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Moneda</th><th className="py-2 pr-3 text-right">Saldo</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {cuentas.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cuentas</td></tr>}
              {cuentas.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{c.banco_corto}</td>
                  <td className="py-2 pr-3">{c.alias ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.numero}</td>
                  <td className="py-2 pr-3 capitalize">{c.tipo}</td>
                  <td className="py-2 pr-3">{c.moneda}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.saldo, c.moneda)}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activa' : 'Inactiva'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
